import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Grid } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { styled } from "@mui/material/styles";
import DojoView from "csi/page/DojoView";
import session from "csi/session";
import { useStore } from "csi/state/hooks";
import SaveCancelButtons from "designSystem/components/button/SaveCancelButtons";
import LinkIcon from "designSystem/components/icon/LinkIcon";
import LinkToggleIcon from "designSystem/components/icon/LinkToggleIcon";
import PaneHeader from "designSystem/components/pane/PaneHeader";
import ResizeHandle from "designSystem/components/pane/ResizeHandle";
import { Box, Button, Chip, DataGrid, DataGridCardProps, FlexColumn, FlexRowCenter, IconButton, Link, Message, OverflowText, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { black, blue, grey, white } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { C8Node } from "src/c8-platform/type/node";
import { INFO_PANEL_MIN_WIDTH, useLightboxStore } from "./LightboxStore";

interface SectionHeaderProps {
	/** indicate collapsed or expanded state of the section header */
	collapsed: boolean;

	/** indicate whether to show the title label suffix */
	suffix: boolean;

	/** title label of the section header */
	titleRes: string;

	/** function to toggle the collpased/expanded state */
	updateCollapsed: () => void;
}

const LabelChip = styled(Chip)(({ theme }) => ({
	marginBottom: theme.spacing(1),
	marginRight: theme.spacing(1),
	marginLeft: theme.spacing(0),
}));

const StyledLinkAllOverflowText = styled(OverflowText)(({ theme }) => ({
	borderRightColor: grey[400],
	borderRightStyle: "solid",
	borderRightWidth: 1,
	margin: "0px 5px",
	paddingRight: "5px",
}));

const PREFIX = "LBInfoPanel";

const classes = {
	root: `${PREFIX}root`,
	gap: `${PREFIX}gap`,
	left: `${PREFIX}left`,
	leftBottom: `${PREFIX}leftBottom`,
	leftLink: `${PREFIX}leftLink`,
	right: `${PREFIX}right`,
};

const WhereUsedCardDiv = styled("div")(({
	theme,
}) => ({
	[`&.${classes.root}`]: {
		display: "flex",
		flexDirection: "row",
		gap: "16px",
		width: "100%",
	},

	[`& .${classes.left}`]: {
		flexGrow: 1,
		width: "50%",
	},

	[`& .${classes.leftBottom}`]: {
		marginTop: "2px",
	},

	[`& .${classes.leftLink}`]: {
		maxWidth: "100%",
	},

	[`& .${classes.right}`]: {
		display: "flex",
		flexDirection: "column",
		gap: 4,
		textAlign: "end",
		width: "50%",
	},
}));

const getInfoPanelWidth = (width: number): number => {
	return Math.min(Math.max(INFO_PANEL_MIN_WIDTH, width), INFO_PANEL_MIN_WIDTH * 2);
};

const SectionHeader: FunctionComponent<SectionHeaderProps> = observer((props) => {
	const { collapsed, suffix, titleRes, updateCollapsed } = props;
	const {
		imageWhereUsedCount,
	} = useLightboxStore();

	return (
		<Box
			alignItems="center"
			aria-label={collapsed ? "expand" : "collapse"}
			borderBottom={collapsed ? STANDARD_BORDER_STYLE : "0px"}
			display="flex"
			onClick={updateCollapsed}
			sx={{
				padding: "16px 16px 16px 12px",
			}}
		>
			<OverflowText
				bold
				color={grey["A100"]}
				grow={!suffix}
				res={titleRes}
				variant="overline"
			/>
			{suffix && (
				<OverflowText
					bold
					color={grey["A100"]}
					grow
					sx={{ ml: 1 }}
					text={`(${imageWhereUsedCount})`}
					variant="overline"
				/>
			)}
			<Box ml={1}>
				{collapsed ? <ExpandMore /> : <ExpandLess />}
			</Box>
		</Box>
	);
});

const ImageAttributesView = observer(() => {
	const { attributesViewParams, imageNodeUrl, infoPanelCtr } = useLightboxStore();
	return (
		<DojoView
			className="csi-dojo-lightbox-info-panel-context-card"
			forceRefresh={infoPanelCtr}
			maintainHeightOnRefresh
			nodeUrl={imageNodeUrl}
			viewId={attributesViewParams.defView.m_id}
			viewParams={attributesViewParams}
		/>
	);
});

const ImageAttributes = observer(() => {
	const { imageAttributesCollapse, toggleCollapsedImageAttr } = useLightboxStore();

	return (
		<Box>
			<SectionHeader
				collapsed={imageAttributesCollapse}
				suffix={false}
				titleRes='ImageAttributes'
				updateCollapsed={toggleCollapsedImageAttr}
			/>
			<ImageAttributesContent />
		</Box>
	);
});

const ImageAttributesContent = observer(() => {
	const { imageAttributesCollapse } = useLightboxStore();

	return (
		<Collapse in={!imageAttributesCollapse} timeout="auto">
			<Box borderBottom={STANDARD_BORDER_STYLE} pb={4}>
				<FlexRowCenter
					gap={3}
					px={1}
				>
					<Box
						growNoOverflow
						textAlign="left"
					>
						<ImageAttributesView />
					</Box>
				</FlexRowCenter>
			</Box>
		</Collapse>
	);
});

export const ImageWhereUsedCard: FunctionComponent<DataGridCardProps<C8Node>> = observer((props) => {
	const { item } = props;
	const { $Type, $URL, __Parent__ } = item;

	const {
		cache,
		currentImage,
		editModeInfoPanel,
		toggleWhereUsedLink,
		unlinkedWhereUsed,
	} = useLightboxStore();

	const { containerUrl } = currentImage.data;
	const isImageContainerNode = (containerUrl === $URL);
	const parent = cache.get(item.__Parent__);
	const loc = useLocale();
	const displayLinkIcon = !unlinkedWhereUsed.has($URL) ? <LinkIcon /> : <LinkToggleIcon />;

	return (
		<Box marginBottom={4} width="100%">
			<FlexColumn gap={1}>
				<FlexRowCenter gap={2} growNoOverflow>
					<WhereUsedCardDiv className={classes.root}>
						<div className={classes.left} id="whereUsedCardLeft">
							{!isImageContainerNode ? (
								<>
									<div>
										<Link
											className={classes.leftLink}
											grow
											href={session.getLocationForUrl($URL)}
											target="_blank"
										>
											<OverflowText bold text={loc.getNodeName(item)} />
										</Link>
									</div>
									<div className={classes.leftBottom}>
										<Link
											className={classes.leftLink}
											grow
											href={session.getLocationForUrl(__Parent__)}
											target="_blank"
										>
											<OverflowText text={loc.getNodeName(parent)} />
										</Link>
									</div>
								</>
							)
								: (
									<>
										<OverflowText bold text={loc.getNodeName(item)} />
										<OverflowText
											grow
											mt={0.5}
											paddingY={1.5}
											text={loc.getNodeName(parent)}
										/>
									</>
								)}
						</div>
						<div className={classes.right} id="whereUsedCardRight">
							{!isImageContainerNode ? (
								<>
									{editModeInfoPanel ? (
										<span>
											<IconButton
												design="iconOnlyOnColor"
												onClick={() => toggleWhereUsedLink($URL)}
												size="small"
											>
												{displayLinkIcon}
											</IconButton>
										</span>
									)
										: (
											<span>
												{displayLinkIcon}
											</span>
										)}
								</>
							)
								:
								<span>&nbsp;</span>
							}
							<OverflowText flexShrink={0} text={loc.type($Type)} />
						</div>
					</WhereUsedCardDiv>
				</FlexRowCenter>
			</FlexColumn>
		</Box>
	);
});

const ImageWhereUsed = observer(() => {
	const {
		imageWhereIsUsedCollapse,
		toggleCollapsedImageWhereUsed,
	} = useLightboxStore();

	return (
		<Box>
			<SectionHeader
				collapsed={imageWhereIsUsedCollapse}
				suffix
				titleRes='ImageWhereIsUsed'
				updateCollapsed={toggleCollapsedImageWhereUsed}
			/>
			<ImageWhereUsedContent />
		</Box>
	);
});

const ImageWhereUsedContent = observer(() => {
	const {
		dataStore,
		editModeInfoPanel,
		linkUnlinkAllMode,
		imageWhereIsUsedCollapse,
		toggleLinkUnlinkAllImageWhereUsed,
	} = useLightboxStore();

	const { activateLinkAll, activateUnLinkAll } = linkUnlinkAllMode;

	return (
		<Collapse in={!imageWhereIsUsedCollapse} timeout="auto">
			<Box
				pl={3}
				pr={4}
			>
				{editModeInfoPanel && (
					<>
						<Message titleRes="ImageSaveMsg" variant="info" />
						<FlexRowCenter mb={2} mt={3.5}>
							<Box flexGrow={1} />
							<StyledLinkAllOverflowText
								onClick={() => activateLinkAll && toggleLinkUnlinkAllImageWhereUsed("linkAll")}
								res="LinkAll"
								sx={{
									color: activateLinkAll ? blue[600] : grey[700],
									cursor: activateLinkAll ? "pointer" : "default",
								}}
							/>
							<OverflowText
								onClick={() => activateUnLinkAll && toggleLinkUnlinkAllImageWhereUsed("unLinkAll")}
								res="UnLinkAll"
								sx={{
									color: activateUnLinkAll ? blue[600] : grey[700],
									cursor: activateUnLinkAll ? "pointer" : "default",
								}}
							/>
						</FlexRowCenter>
					</>
				)}
				<FlexRowCenter>
					<Box flexGrow={1}>
						<DataGrid
							CardComponent={ImageWhereUsedCard}
							columns={1}
							infiniteScrolling
							rowsPerPage={50}
							store={dataStore}
						/>
					</Box>
				</FlexRowCenter>
			</Box>
		</Collapse>
	);
});

const ImageGrid = observer(function ImageGrid() {
	const {
		currentImage,
		getLocalizedImageLabels,
	} = useLightboxStore();

	const { name, dateCreated, imageSize, imageType, width, height } = currentImage.data;
	const loc = useLocale();
	const imageLabels = getLocalizedImageLabels(loc);
	return (
		<Box borderBottom={STANDARD_BORDER_STYLE} pb={4}>
			<FlexRowCenter
				sx={{
					padding: "0px 12px 0px 12px",
				}}
			>
				<Grid
					color={black[200]}
					container
					spacing={2}
				>
					<Grid item xs={6}>
						<Text res="ImageName" />
					</Grid>
					<Grid item xs={6}>
						<OverflowText text={name} />
					</Grid>
					<Grid item xs={6}>
						<Text res="DateCreated" />
					</Grid>
					<Grid item xs={6}>
						<Text text={dateCreated} />
					</Grid>
					<Grid item xs={6}>
						<Text res="ViewPreferences_ImageSize" />
					</Grid>
					<Grid item xs={6}>
						<Text text={imageSize} />
					</Grid>
					<Grid item xs={6}>
						<Text res="ImageDimension" />
					</Grid>
					<Grid item xs={6}>
						<Text text={`${width} X ${height} PX`} />
					</Grid>
					<Grid item xs={6}>
						<Text res="ImageType" />
					</Grid>
					<Grid item xs={6}>
						<Text text={imageType} uppercase />
					</Grid>
					<Grid item xs={6}>
						<Text res="ImageIntentLabels" />
					</Grid>
					<Grid item xs={6}>
						{imageLabels.length > 0 ?
							imageLabels.map((label, i) => (
								<LabelChip
									disabled
									key={i}
									label={label}
								/>
							))
							:
							<Text res="NoLabelAssigned" />}
					</Grid>
				</Grid>
			</FlexRowCenter>
		</Box>
	);
});

const ImageInformation = observer(function ImageInformation() {
	const {
		imageInformationCollapse,
		toggleCollapsedImageInfo,
	} = useLightboxStore();
	return (
		<Box>
			<SectionHeader
				collapsed={imageInformationCollapse}
				suffix={false}
				titleRes='ImageInformation'
				updateCollapsed={toggleCollapsedImageInfo}
			/>
			<Collapse in={!imageInformationCollapse} timeout="auto">
				<ImageGrid />
			</Collapse>
		</Box>
	);
});

const ImageSaveCancel = observer(() => {
	const {
		cancelChanges,
		imageInfoPanelSaveLabelParams,
		infoPanelHasBeenUpdated,
		saveChanges,
	} = useLightboxStore();

	const { saveLabelRes, saveLabelResParams } = imageInfoPanelSaveLabelParams;

	return (
		<SaveCancelButtons
			cancelButtonDesign="secondary"
			grow
			onCancel={cancelChanges}
			onSave={saveChanges}
			saveButtonDisabled={!infoPanelHasBeenUpdated}
			saveLabelRes={saveLabelRes}
			saveLabelResParams={saveLabelResParams}
			spacingAround={2}
		/>
	);
});

const ImageEditSaveCancel = observer(() => {
	const {
		editModeInfoPanel, 
		toggleEditModeInfoPanel,
	} = useLightboxStore();

	return (
		<FlexRowCenter
			borderTop={STANDARD_BORDER_STYLE}
			pl={editModeInfoPanel ? 1 : 4.5}
			pr={editModeInfoPanel ? 2.5 : 4.5}
			py={2}
		>
			{editModeInfoPanel ?
				<ImageSaveCancel />
				: (
					<Button
						design="secondary"
						fullWidth
						onClick={toggleEditModeInfoPanel}
						res="Edit"
					/>
				)}
		</FlexRowCenter>
	);
});

export const LBInfoPanel: FunctionComponent = observer(() => {
	const {
		canEditImageAttributes,
		showImageAttributes,
		showImageWhereUsed,
		toggleInfoPanel,
	} = useLightboxStore();

	const { localStore } = useStore();
	const { lightboxInfoPanelWidth } = localStore;

	const onResizeComponent = action((delta: number, dragging: boolean) => {
		localStore.lightboxInfoPanelWidth = getInfoPanelWidth(lightboxInfoPanelWidth - delta);
	});

	return (
		<>
			<ResizeHandle onResize={onResizeComponent} />
			<FlexColumn
				bgcolor={white[50]}
				flexGrow={1}
				flexShrink={0}
				width={getInfoPanelWidth(localStore.lightboxInfoPanelWidth)}
			>
				<PaneHeader onClose={toggleInfoPanel} titleRes="Information" />
				<FlexColumn flexGrow={1} overflow="auto">
					<ImageInformation />
					{showImageAttributes &&
						<ImageAttributes />
					}
					{showImageWhereUsed &&
						<ImageWhereUsed />
					}
				</FlexColumn>
				{canEditImageAttributes &&
						<ImageEditSaveCancel />
				}
			</FlexColumn>
		</>
	);
});

export default LBInfoPanel;